import { IForm } from 'core-components/classes/models/IForm'

import { ICheckedValue } from '../types'

type TDataIds = string | number

export enum TCheckedValues {
  default = 'default',
  withChildren = 'withChildren',
}

interface ISourceFields {
  keyField: string
  hierarchyField?: string
  hierarchyRoot?: string | number | null
}

export interface IAdditionalFields {
  fields: ISourceFields
  checkedValues: ICheckedValue[]
  setCheckedValues: (newCheckedValues: ICheckedValue[]) => void
}

const recalculateCheckbox = (
  type: TCheckedValues,
  dataValue: IForm,
  checkedValues: ICheckedValue[],
  newCheckedValues: ICheckedValue[],
  changedChildrenIds: TDataIds[],
  items: IForm[],
  keyField: string,
  hierarchyField: string,
  visited: Set<TDataIds> = new Set([dataValue[keyField]])
) => {
  const children = items.filter(
    (item) =>
      hierarchyField &&
      item[hierarchyField] === dataValue[keyField] &&
      !visited.has(item[keyField])
  )
  if (!children.length) {
    const checkedValue = checkedValues.find((el) => el.key === dataValue[keyField])
    const newValue = {
      childCount: 0,
      childChecked: 0,
    }
    newCheckedValues.push({
      key: dataValue[keyField],
      value: checkedValue ? checkedValue.value : 'empty',
    } as ICheckedValue)
    return newValue
  }
  children.forEach((child) => {
    visited.add(child[keyField])
  })
  const countData = { childCount: 0, childChecked: 0 }
  children.forEach((child) => {
    const result = recalculateCheckbox(
      type,
      child,
      checkedValues,
      newCheckedValues,
      changedChildrenIds,
      items,
      keyField,
      hierarchyField,
      visited
    )
    countData.childCount += result.childCount
    countData.childChecked += result.childChecked
    const childCheckedValue = newCheckedValues.find((el) => el.key === child[keyField])
    if (childCheckedValue && childCheckedValue.value === 'full') {
      countData.childChecked += 1
    }
  })
  countData.childCount += children.length

  let childValue = 'empty'

  if (countData.childChecked !== 0) {
    childValue = 'indeterminate'
  }

  if (countData.childCount === countData.childChecked && countData.childChecked !== 0) {
    childValue = 'full'
    const childrenIds = children.map((el) => el[keyField])
    changedChildrenIds.push(...childrenIds)
  }

  const newValue = {
    childCount: countData.childCount,
    childChecked: childValue === 'full' ? countData.childCount : countData.childChecked,
  }

  newCheckedValues.push({
    key: dataValue[keyField],
    value: childValue,
  } as ICheckedValue)
  return newValue
}

const recalculateCheckboxes = (
  type: TCheckedValues,
  checkedValues: ICheckedValue[],
  items: IForm[],
  keyField: string,
  hierarchyField: string,
  hierarchyRoot: string | number | null
) => {
  const parents = items.filter((item) => item[hierarchyField] === hierarchyRoot)
  const newCheckedValues: ICheckedValue[] = []
  const changedChildrenIds: TDataIds[] = []
  parents.forEach((item) => {
    recalculateCheckbox(
      type,
      item,
      checkedValues,
      newCheckedValues,
      changedChildrenIds,
      items,
      keyField,
      hierarchyField
    )
  })
  return newCheckedValues.map((el) => {
    if (changedChildrenIds.includes(el.key)) {
      return { ...el, value: 'full' }
    }
    return el
  }) as ICheckedValue[]
}

const getChilds = (
  childs: IForm[],
  currentItem: IForm,
  items: IForm[],
  hierarchyField: string,
  keyField: string,
  visited: Set<TDataIds> = new Set([currentItem[keyField]])
) => {
  const currentItemChilds = items.filter(
    (el) => el[hierarchyField] === currentItem[keyField] && !visited.has(el[keyField])
  )
  if (currentItemChilds.length) {
    currentItemChilds.forEach((el) => {
      visited.add(el[keyField])
    })
    currentItemChilds.forEach((el) => {
      const itemChilds = getChilds(childs, el, items, hierarchyField, keyField, visited)
      childs.push(...itemChilds)
    })
  }
  return currentItemChilds
}

const getChildrenCheckedValues = (
  selectedItem: IForm,
  value: boolean,
  items: IForm[],
  type: TCheckedValues,
  hierarchyField: string,
  keyField: string
) => {
  const childsOfChilds: IForm[] = []
  const rootChilds = getChilds(childsOfChilds, selectedItem, items, hierarchyField, keyField)
  const childsIds = [...rootChilds, ...childsOfChilds].map((el) => el[keyField])
  let valueForChildren = 'empty'
  if (type === TCheckedValues.withChildren && value) {
    valueForChildren = 'full'
  }
  return childsIds.map((el) => ({ key: el, value: valueForChildren }) as ICheckedValue)
}

const replaceValue = (
  selectedItem: IForm,
  value: boolean,
  additionalFields: IAdditionalFields,
  newValues: ICheckedValue[]
) => {
  const { checkedValues, fields } = additionalFields
  const { keyField } = fields
  const valueIndex = checkedValues.findIndex((el) => el.key === selectedItem[keyField])
  if (valueIndex === -1) {
    newValues.push({ key: selectedItem[keyField], value: value ? 'full' : 'empty' })
  } else {
    newValues[valueIndex] = { key: selectedItem[keyField], value: value ? 'full' : 'empty' }
  }
}

export const onDefaultCheck = (
  changedData: IForm | IForm[],
  value: boolean,
  items: IForm[],
  additionalFields: IAdditionalFields
) => {
  const { checkedValues, setCheckedValues } = additionalFields
  const newValues = [...checkedValues]
  if (Array.isArray(changedData)) {
    changedData.forEach((item) => {
      replaceValue(item, value, additionalFields, newValues)
    })
  } else {
    replaceValue(changedData, value, additionalFields, newValues)
  }
  setCheckedValues(newValues)
  return newValues
}

export const onWithChildrenCheck = (
  changedData: IForm | IForm[],
  value: boolean,
  items: IForm[],
  additionalFields: IAdditionalFields
) => {
  const { checkedValues, fields, setCheckedValues } = additionalFields
  const { keyField, hierarchyField, hierarchyRoot } = fields
  const newValues = [...checkedValues]
  if (Array.isArray(changedData)) {
    changedData.forEach((item) => {
      replaceValue(item, value, additionalFields, newValues)
    })
  } else {
    replaceValue(changedData, value, additionalFields, newValues)
  }
  if (hierarchyRoot !== undefined && hierarchyField) {
    const valueForRecalculate = [...newValues]
    if (!Array.isArray(changedData)) {
      const childrenCheckedValues = getChildrenCheckedValues(
        changedData,
        value,
        items,
        TCheckedValues.withChildren,
        hierarchyField,
        keyField
      )
      childrenCheckedValues.forEach((child) => {
        const childIndex = newValues.findIndex((value) => child.key === value.key)
        if (childIndex === -1) {
          valueForRecalculate.push(child)
        } else {
          valueForRecalculate[childIndex] = child
        }
      })
    }

    const recalculatedCheckedValues = recalculateCheckboxes(
      TCheckedValues.withChildren,
      valueForRecalculate,
      items,
      keyField,
      hierarchyField,
      hierarchyRoot
    )
    setCheckedValues(recalculatedCheckedValues)
    return recalculatedCheckedValues
  }
  setCheckedValues(newValues)
  return newValues
}
